import { FunctionComponent, useState, useEffect, useRef } from 'react';

import Flex from '../../Flex';
import MonthInput from '../Input/MonthInput';
import Button from '../../Button';
import Icon, { registerIcon } from '../../Icon';
import * as caretLeftIcon from '../../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../../Icon/icons/caret-right.icon';
import { useConfiguration, useDirection, useI18n } from '../../../hooks';
import { getCalendarTypeFromLocale, toUTCDateString } from '../Input/utils';
import { CALENDAR_TYPES } from '../Input/local';

import Calendar from './Calendar';
import DatePickerProps from './DatePicker.types';
import { ScreenReaderOnly, StyledDatePicker, StyledMonthPicker } from './Calendar.styles';
import { isValidDate } from './utils';

registerIcon(caretLeftIcon, caretRightIcon);

const defaultProps: Partial<DatePickerProps> = {
  showWeekNumber: false,
  mode: 'date'
};

function toDateString(date: Date | undefined) {
  return date ? toUTCDateString(date) : undefined;
}

const DatePicker: FunctionComponent<DatePickerProps> = (props: DatePickerProps) => {
  const { locale } = useConfiguration();
  const { selected: initial, onChange, min, max, mode, showWeekNumber, ...restProps } = props;

  const [selected, setSelected] = useState(initial);
  const [current, setCurrent] = useState(selected && isValidDate(selected) ? selected : new Date());

  useEffect(() => {
    if (toDateString(initial) !== toDateString(selected)) {
      setSelected(initial);
      setCurrent(initial || new Date());
    }
  }, [toDateString(initial)]);

  const calendarRef = useRef<HTMLTableElement>(null);
  useEffect(() => {
    // need to postpone focusing to make sure onFocusWithin has been attached
    setTimeout(() => {
      calendarRef.current?.querySelector<HTMLElement>('button[tabindex="0"]:enabled')?.focus();
    });
  }, []);

  const calendarType = getCalendarTypeFromLocale(locale);
  const t = useI18n();
  const { start, end } = useDirection();

  return (
    <StyledDatePicker {...restProps}>
      <Flex as={StyledMonthPicker} container={{ justify: 'between', alignItems: 'center' }}>
        <Button
          variant='simple'
          icon
          onClick={() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCMonth(newCurrent.getUTCMonth() - 1);
            setCurrent(newCurrent);
          }}
          aria-label={t('picker_prev_month')}
        >
          <Icon name={`caret-${start}`} />
        </Button>
        <MonthInput
          value={current}
          required
          aria-label={t('picker_jump_to_month')}
          onChange={({ valueAsTimestamp }) => {
            const newCurrent = new Date(valueAsTimestamp!);
            newCurrent.setUTCDate(current.getUTCDate());
            setCurrent(newCurrent);
          }}
          min={min}
          max={max}
        />
        <ScreenReaderOnly aria-live='polite'>
          {current.toLocaleString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })}
        </ScreenReaderOnly>
        <Button
          variant='simple'
          icon
          onClick={() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCMonth(newCurrent.getUTCMonth() + 1);
            setCurrent(newCurrent);
          }}
          aria-label={t('picker_next_month')}
        >
          <Icon name={`caret-${end}`} />
        </Button>
      </Flex>
      <Flex container={{ wrap: 'nowrap' }} dir='ltr'>
        <Calendar
          ref={calendarRef}
          selected={selected && isValidDate(selected) ? selected : undefined}
          current={current}
          calendarType={mode === 'date' ? calendarType : CALENDAR_TYPES.ISO_8601}
          showWeekNumber={showWeekNumber}
          mode={mode}
          onFocus={date => {
            setCurrent(date);
          }}
          onSelect={date => {
            setCurrent(date);
            setSelected(date);
            onChange?.(date);
          }}
          min={min}
          max={max}
        />
      </Flex>
    </StyledDatePicker>
  );
};

DatePicker.defaultProps = defaultProps;

export default DatePicker;
